import React, { useCallback, useRef } from 'react';
import { useMatch } from 'react-router-dom';
import styled from 'styled-components';
import {
    FeatureCard,
    Group,
    Text,
    fontSize,
    palette,
    spacing,
    Badge,
} from '@landr/maestro';
import { t } from '@lingui/macro';
import { validate as uuidValidate } from 'uuid';
import { analytics } from 'utils/analytics';
import { StreamEntityType, RoutePaths } from 'types/enums';
import {
    getImageUrl,
    getPackRouteEntityPath,
    getStreamEntityType,
} from 'helpers';
import PlayerButton from 'components/CommonUi/PlayerButton/PlayerButton';
import { LinkWrapper } from 'components/Cards/BaseCard/styled';
import { useGenericCarouselContext } from 'components/GenericCarousel';
import { AddSessionToCreator } from 'components/CommonUi/AddSessionToCreator';
import ExclusivePackBadge from 'components/ExclusiveCardBadge/ExclusiveCardBadge';
import { useTrackingContext } from 'contexts/TrackingContext/TrackingContext';
import { CommonCardProps, Pack, RePack } from 'types';
import { FavoriteButton } from 'components/FavoriteButton';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { withInteractionSource } from 'contexts/InteractionSourceContext';
import { useTakenDownPack } from 'hooks/useTakenDown/useTakenDown';
import { useDownloadsPageMatch } from 'hooks/useDownloadsPageMatch/useDownloadsPageMatch';
import { DownloadButton } from 'components/CommonUi/DownloadButton';
import addQueryStringTo from 'helpers/addQueryStringTo';
import { FeatureFlag } from 'types/enums';
import { useFlag } from 'hooks/useFlag/useFlag';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { PlayerProvider } from 'contexts/PlayerContext/PlayerContext';

export interface PackCardProps extends CommonCardProps<Pack | RePack> {
    page?: number;
    position?: number;
}

export const PackCardTitle = styled(Text)`
    font-size: ${fontSize('sm')};
    color: ${palette('neutral.50')};
    margin-top: ${spacing('sm')};
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: normal;
    position: relative;
`;

const ContentWrapper = styled.div`
    position: relative;
`;

const PackCardComponent: React.FC<PackCardProps> = ({
    item,
    page,
    position,
    isSelected,
}) => {
    const hasPersistentSearchFF = useFlag(FeatureFlag.PersistentSearch);
    const buttonRef = useRef<HTMLDivElement>(null);

    const toLocalizedPath = useToLocalizedPath();

    const { carouselName, carouselId } = useGenericCarouselContext();
    const {
        getPackCardPlayButtonClickTracker,
        getPackPlayButtonClickTrackerV2,
    } = useTrackingContext();

    const { isTakenDown } = useTakenDownPack(item);
    const { isDownloadsPage } = useDownloadsPageMatch();
    const isTakenDownUIVisible = isTakenDown && isDownloadsPage;

    const entityPath = getPackRouteEntityPath(item);
    const streamEntityTypeName: StreamEntityType = getStreamEntityType(
        item.type,
    );

    const showOpenInCreator =
        item?.creatorSession && uuidValidate(item.creatorSession);

    const onPlayButtonClick = useCallback(
        (totalTime) => {
            getPackCardPlayButtonClickTracker({
                carouselName,
                carouselId,
                item,
                page,
                position,
            })();

            getPackPlayButtonClickTrackerV2({
                interactionSource: InteractionSource.CardAction,
                item,
            })(totalTime);
        },
        [
            carouselName,
            carouselId,
            item,
            page,
            position,
            getPackCardPlayButtonClickTracker,
            getPackPlayButtonClickTrackerV2,
        ],
    );

    const label = item?.isDownloaded ? t`Download` : t`Get pack`;

    let actions = [
        <PlayerButton
            id={item.guid}
            callback={onPlayButtonClick}
            size="sm"
            variant="subtle"
            isRounded
            isDemo
            key={'player-button'}
            keyboardEventConsumerRef={buttonRef}
        />,
        <DownloadButton
            item={item}
            size="sm"
            variant="subtle"
            isRounded
            key={'download-button'}
            label={label}
        />,
    ];

    if (!isTakenDownUIVisible) {
        actions = [
            ...actions,
            <FavoriteButton
                key={`reconciliation-isFavorited-${item.isFavorited}`}
                size="sm"
                variant="subtle"
                isRounded
                data-testid="favorite-pack-button"
                item={item}
            />,
        ];
    }

    if (showOpenInCreator) {
        actions = [
            ...actions,
            <AddSessionToCreator
                sessionGuid={item?.creatorSession}
                size="sm"
                variant="subtle"
                isRounded
                interactionSource="Creator Collection card"
                key={'creator-button'}
            />,
        ];
    }

    function onCardClick(): void {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewDetail,
            ctaType: CtaType.Card,
            interactionSource: InteractionSource.CardAction,
            ctaText: carouselName
                ? `${carouselName} - ${item.name}`
                : item.name,
            ctaDestination: toLocalizedPath(`/${entityPath}/${item.slug}`),
        });
    }

    const isPackOrRepackDetailsPage =
        !!useMatch(toLocalizedPath(`/${RoutePaths.Packs}/:slug`)) ||
        !!useMatch(toLocalizedPath(`/${RoutePaths.RePacks}/:slug`));

    const pathname = toLocalizedPath(`/${entityPath}/${item.slug}`);
    const to = addQueryStringTo(
        pathname,
        !isPackOrRepackDetailsPage && hasPersistentSearchFF,
    );

    return (
        <PlayerProvider
            playerId={item.guid}
            streamEntityType={streamEntityTypeName}
        >
            <ContentWrapper>
                <LinkWrapper
                    onClick={onCardClick}
                    to={to}
                    data-testid="pack-card-body"
                >
                    <FeatureCard
                        data-testid="feature-card"
                        data-isdownloaded={item.isDownloaded}
                        as={Group.Item}
                        shape="square"
                        backgroundImage={getImageUrl({
                            imageUrl: item.artworkUrl,
                            width: 192,
                        })}
                        backgroundColor={item.backgroundColor}
                        actions={actions}
                        isSelected={isSelected}
                        badges={
                            isTakenDownUIVisible
                                ? [
                                      <Badge
                                          variant="warning"
                                          kind="subtle"
                                          content={t`unavailable`}
                                          size="sm"
                                          isRounded
                                      />,
                                  ]
                                : []
                        }
                    />
                </LinkWrapper>
                <PackCardTitle>
                    <LinkWrapper
                        onClick={onCardClick}
                        to={to}
                        data-testid="pack-card-title"
                    >
                        {item.name}
                    </LinkWrapper>
                </PackCardTitle>
                {item.isExclusive && <ExclusivePackBadge isAbsolute={true} />}
            </ContentWrapper>
        </PlayerProvider>
    );
};

export const PackCard = withInteractionSource(
    PackCardComponent,
    InteractionSource.CardAction,
);
